import { ChangeEvent, FormEvent, useCallback, useState } from "react"
import { MetricFilterOperator } from "../../TopPerformingAds/__generated__/TopPerformingAdsQuery.graphql"
import { FilterItemProps, inputClasses } from "../_common"
import clsx from "clsx"
import { CheckIcon, PencilIcon } from "@heroicons/react/24/outline"
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
interface MetricFilterBase {
    fieldKey: string
    operator: MetricFilterOperator
    value: number
}
interface MetricsFilterProps<T extends MetricFilterBase>
    extends FilterItemProps<T[]> {
    fieldOptions: T['fieldKey'][]
    renderOptionLabel: (opt: T['fieldKey']) => string
    useFilterIcon: boolean
}
export function MetricFilter<
    MetricFilterType extends MetricFilterBase,
>({
    value,
    handleChange,
    fieldOptions,
    renderOptionLabel,
    useFilterIcon,
}: MetricsFilterProps<MetricFilterType>) {
    const [stagedMetricFilter, setStagedMetricFilter] =
        useState<Partial<MetricFilterType>>()
    const [editingIndices, setEditingIndices] = useState<number[]>([])

    const onStagedFormInputChange = useCallback(
        (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
            const target = e.currentTarget
            const { name, value } = target
            if (!name && !value) return

            if (name === 'value') {
                const nextState = {
                    value: parseFloat(value),
                } as Partial<MetricFilterType>

                setStagedMetricFilter((prev) => ({ ...prev, ...nextState }))
            } else {
                const nextState = {
                    [name]: value,
                } as Partial<MetricFilterType>
                setStagedMetricFilter((prev) => ({ ...prev, ...nextState }))
            }
        },
        []
    )

    const handleSave = useCallback(() => {
        if (!isValidMetricFilter(stagedMetricFilter)) return
        const nextValue = [...(value || []), stagedMetricFilter]
        handleChange?.(nextValue)
        setStagedMetricFilter(undefined)
    }, [handleChange, stagedMetricFilter, value])

    const handleExistingEditSave = useCallback((e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!(e.target instanceof HTMLFormElement)) return
        const strIndex = e.target.getAttribute('data-index')
        if (!strIndex) return

        const fd = new FormData(e.target)
        const values = {
            fieldKey: fd.get('fieldKey') as string,
            operator: fd.get('operator') as MetricFilterOperator,
            value: parseFloat(fd.get('value') as string),
        } as MetricFilterType

        const index = parseInt(strIndex)
        const nextValue = value?.map((v, i) => i === index ? values : v) || []
        handleChange?.(nextValue)
        setEditingIndices((prev) => prev.filter((i) => i !== index))

    }, [handleChange, value]);

    return (
        <div className="flex flex-col items-end" style={{flexDirection: "row", alignItems: "center"}}>
            {(value && value!.length > 0) ? (
                <div className="mb-2 flex-col items-end gap-1" style={{marginRight: "25px"}}>
                    {value?.map((filter, index) => (
                        <form
                            key={`${filter.fieldKey}_${filter.operator}_${filter.value}`}
                            className="flex items-center justify-end gap-2"
                            onSubmit={handleExistingEditSave}
                            data-index={index}
                        >
                            {editingIndices.includes(index) ? (

                                <>
                                    <select
                                        defaultValue={filter.fieldKey || ""}
                                        name="fieldKey"
                                        className={clsx(inputClasses, 'w-max pr-6 text-xs')}
                                    >
                                        <option value="" disabled>
                                            Select Field
                                        </option>
                                        {fieldOptions.map((opt) => (
                                            <option value={opt} key={opt}>{renderOptionLabel(opt)}</option>
                                        ))}
                                    </select>
                                    <select
                                        defaultValue={filter.operator || ""}
                                        name="operator"
                                        className={clsx(inputClasses, 'w-min pr-6 text-xs')}
                                    >
                                        <option value="" disabled>
                                            Op
                                        </option>
                                        {METRIC_FILTER_OPERATOR_OPTIONS.map((opt) => (
                                            <option value={opt} key={opt}>{METRIC_FILTER_OPERATOR_LABELS[opt]}</option>
                                        ))}
                                    </select>
                                    <input
                                        placeholder="Value"
                                        defaultValue={filter.value}
                                        name="value"
                                        type="number"
                                        step="0.0001"
                                        className={clsx(inputClasses, 'w-24 text-xs')}
                                    />
                                    <button className="text-trueBlue cursor-pointer text-base hover:underline p-2"
                                        type="submit"
                                    >
                                        <CheckIcon className="w-5 h-5" />
                                    </button>
                                </>

                            ) : (
                                <>
                                    <span className="text-midGray text-sm">
                                        {renderOptionLabel(filter.fieldKey)}{' '}
                                        {METRIC_FILTER_OPERATOR_LABELS[filter.operator]} {filter.value}
                                    </span>
                                    <button className="text-trueBlue cursor-pointer text-base hover:underline p-2"
                                        type="button"
                                        onClick={() => {
                                            setEditingIndices(prev => [...new Set([...prev, index])])
                                        }}>
                                        <PencilIcon className="w-3 h-3" />
                                    </button>

                                    <button
                                        type="button"
                                        className="text-trueBlue cursor-pointer text-base hover:underline p-1"
                                        onClick={() =>
                                            handleChange?.(value?.filter((_, i) => i !== index) || [])
                                        }
                                    >
                                        x
                                    </button>
                                </>
                            )}
                        </form>
                    ))}
                </div>
                ) : <></>
            }
            
            {
                stagedMetricFilter ? (
                    <div className="flex items-center gap-2">
                        <select
                            value={stagedMetricFilter.fieldKey || ""}
                            name="fieldKey"
                            className={clsx(inputClasses, 'w-max pr-6 text-xs')}
                            onChange={onStagedFormInputChange}
                        >
                            <option value='' disabled>
                                Select Field
                            </option>
                            {fieldOptions.map((opt) => (
                                <option value={opt} key={opt}>{renderOptionLabel(opt)}</option>
                            ))}
                        </select>
                        <select
                            value={stagedMetricFilter.operator || ""}
                            name="operator"
                            className={clsx(inputClasses, 'w-min pr-6 text-xs')}
                            onChange={onStagedFormInputChange}
                        >
                            <option value='' disabled>
                                Op
                            </option>
                            {METRIC_FILTER_OPERATOR_OPTIONS.map((opt) => (
                                <option value={opt} key={opt}>{METRIC_FILTER_OPERATOR_LABELS[opt]}</option>
                            ))}
                        </select>
                        <input
                            placeholder="Value"
                            value={stagedMetricFilter.value}
                            name="value"
                            type="number"
                            step="0.0001"
                            className={clsx(inputClasses, 'w-24 text-xs')}
                            onChange={onStagedFormInputChange}
                        />

                        <button
                            className="text-midGray cursor-pointer text-xs hover:underline"
                            onClick={() => setStagedMetricFilter(undefined)}
                        >
                            Cancel
                        </button>
                        <button
                            className="text-trueBlue cursor-pointer text-xs hover:underline disabled:cursor-not-allowed disabled:hover:no-underline"
                            onClick={handleSave}
                            disabled={!isValidMetricFilter(stagedMetricFilter)}
                        >
                            Save
                        </button>
                    </div>
                ) : (
                    <button
                        className="text-trueBlue cursor-pointer text-xs hover:underline"
                        onClick={() => setStagedMetricFilter({})}
                    >
                        {useFilterIcon ? <FilterAltOutlinedIcon /> : "(+) Add Metric Filter"}
                    </button>

                )
            }
        </div >
    )
}
function isValidMetricFilter<MetricFilterType extends MetricFilterBase>(
    filter: Partial<MetricFilterType> | undefined
): filter is MetricFilterType {
    return (
        !!filter &&
        filter.fieldKey !== undefined &&
        filter.operator !== undefined &&
        filter.value !== undefined
    )
}
const METRIC_FILTER_OPERATOR_OPTIONS: MetricFilterOperator[] = [
    'GTE',
    'GT',
    'LTE',
    'LT',
    'EQ',
    'NE',
]

const METRIC_FILTER_OPERATOR_LABELS: Record<MetricFilterOperator, string> = {
    GTE: '>=',
    GT: '>',
    LTE: '<=',
    LT: '<',
    EQ: '=',
    NE: '!=',
    '%future added value': '',
}